import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useHomeChatActions, useHomeChatState } from '../../context';

// Pages that have their own native chat UI and should not show the global panel.
// Note: '/v2/ucp' (list) intentionally excluded — only contact detail routes have native chat.
export const NATIVE_CHAT_ROUTES = ['/home', '/v2/ucp/', '/v1/ucp/'];

/**
 * Encapsulates all logic for the global floating ChatSlideOut:
 * - On route change with panel open: close without clearing when destination is native
 *   (/home, /v2/ucp/:id); do nothing when destination is non-native (panel stays open).
 * - "Open in Home" handler that preserves the active conversation.
 * - Computes whether the current route is a native-chat page (for the render guard).
 */
export const useGlobalChatPanel = () => {
  const { isGlobalChatOpen } = useHomeChatState();
  const { closeGlobalChat } = useHomeChatActions();
  const navigate = useNavigate();
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);

  const isNativeChatPage = NATIVE_CHAT_ROUTES.some((route) =>
    location.pathname.startsWith(route)
  );

  // On route change while the panel is open: if destination is native, close without clearing;
  // if non-native, keep the panel open (conversation persists).
  useEffect(() => {
    const currentPath = location.pathname;
    if (prevPathnameRef.current === currentPath) return;
    prevPathnameRef.current = currentPath;
    if (!isGlobalChatOpen) return;
    const destIsNative = NATIVE_CHAT_ROUTES.some((r) => currentPath.startsWith(r));
    if (destIsNative) {
      closeGlobalChat();
    }
  }, [location.pathname, isGlobalChatOpen, closeGlobalChat]);

  // "Open in Home": navigate first (preserving conversation in context),
  // then close the panel without clearing — Home will inherit the active conversation.
  const handleOpenInHome = useCallback(() => {
    navigate('/home');
    closeGlobalChat();
  }, [navigate, closeGlobalChat]);

  return {
    isGlobalChatOpen,
    isNativeChatPage,
    closeGlobalChat,
    handleOpenInHome,
  };
};
